﻿import {
  Bold,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Redo,
  Strikethrough,
  Undo,
} from "lucide-react";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "tiptap-markdown";
import { useEffect } from "react";

import { cn } from "@/lib/utils";

/**
 * Organizer legal override editörü. Kaydetme sırasında Markdown'a serileştiriyoruz;
 * backend Markdig ile HTML'e çevirip sanitize eder.
 *
 * Bilinçli olarak resim/iframe/script yok — yasal metin için gereksiz, sanitize
 * edilse bile yanlış yönetim riski taşıyor.
 */
export function TipTapEditor({
  value,
  onChange,
  disabled = false,
  minHeight = "300px",
}: {
  /** İlk render'daki Markdown değeri. `reset` çağrılana kadar güncellemez. */
  value: string;
  onChange: (markdown: string) => void;
  disabled?: boolean;
  minHeight?: string;
}) {
  const editor = useEditor({
    // Turbopack SSR hidration hatalarını önler.
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        // Yasal metinlerde kod bloğu / horizontal rule vb. ihtiyaç yok;
        // menü karmaşası ve Markdown edge case'leri azalsın.
        codeBlock: false,
        horizontalRule: false,
      }),
      Markdown.configure({
        html: false, // raw HTML'i hem input'ta hem output'ta reddet
        tightLists: true,
        bulletListMarker: "-",
        linkify: true,
        breaks: false,
        transformPastedText: true,
      }),
    ],
    content: value,
    editable: !disabled,
    editorProps: {
      attributes: {
        class: cn(
          "tiptap-prose focus:outline-none",
          "p-4 min-h-[var(--tiptap-min-h)]",
        ),
        style: `--tiptap-min-h: ${minHeight}`,
      },
    },
    onUpdate: ({ editor }) => {
      // tiptap-markdown bir `markdown` storage slot ekler; tiptap core
      // Storage interface'i bilmediği için cast ediyoruz.
      const md = (
        editor.storage as unknown as {
          markdown: { getMarkdown(): string };
        }
      ).markdown.getMarkdown();
      onChange(md);
    },
  });

  // disabled dışarıdan değişince editör mode'unu senkronize et.
  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) {
    return (
      <div
        className="rounded-md border bg-muted/30"
        style={{ minHeight }}
        aria-busy="true"
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-md border">
      <Toolbar editor={editor} disabled={disabled} />
      <div className="bg-background">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

function Toolbar({
  editor,
  disabled,
}: {
  editor: Editor;
  disabled: boolean;
}) {
  const btnClass =
    "h-8 w-8 p-0 rounded-sm flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40 disabled:hover:bg-transparent";

  function promptLink() {
    const prev = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Link URL (boş = kaldır):", prev ?? "https://");
    if (url === null) return;
    const trimmed = url.trim();
    if (trimmed === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: trimmed })
      .run();
  }

  const buttons: Array<{
    key: string;
    icon: React.ReactNode;
    isActive: () => boolean;
    run: () => void;
    label: string;
  }> = [
    {
      key: "h1",
      icon: <Heading1 className="h-4 w-4" />,
      isActive: () => editor.isActive("heading", { level: 1 }),
      run: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      label: "H1",
    },
    {
      key: "h2",
      icon: <Heading2 className="h-4 w-4" />,
      isActive: () => editor.isActive("heading", { level: 2 }),
      run: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      label: "H2",
    },
    {
      key: "h3",
      icon: <Heading3 className="h-4 w-4" />,
      isActive: () => editor.isActive("heading", { level: 3 }),
      run: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      label: "H3",
    },
    {
      key: "bold",
      icon: <Bold className="h-4 w-4" />,
      isActive: () => editor.isActive("bold"),
      run: () => editor.chain().focus().toggleBold().run(),
      label: "Kalın",
    },
    {
      key: "italic",
      icon: <Italic className="h-4 w-4" />,
      isActive: () => editor.isActive("italic"),
      run: () => editor.chain().focus().toggleItalic().run(),
      label: "Italik",
    },
    {
      key: "strike",
      icon: <Strikethrough className="h-4 w-4" />,
      isActive: () => editor.isActive("strike"),
      run: () => editor.chain().focus().toggleStrike().run(),
      label: "Üstü çizili",
    },
    {
      key: "bullet",
      icon: <List className="h-4 w-4" />,
      isActive: () => editor.isActive("bulletList"),
      run: () => editor.chain().focus().toggleBulletList().run(),
      label: "Liste",
    },
    {
      key: "ordered",
      icon: <ListOrdered className="h-4 w-4" />,
      isActive: () => editor.isActive("orderedList"),
      run: () => editor.chain().focus().toggleOrderedList().run(),
      label: "Numaralı liste",
    },
    {
      key: "quote",
      icon: <Quote className="h-4 w-4" />,
      isActive: () => editor.isActive("blockquote"),
      run: () => editor.chain().focus().toggleBlockquote().run(),
      label: "Alıntı",
    },
  ];

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b bg-muted/40 p-1">
      {buttons.map((b) => (
        <button
          key={b.key}
          type="button"
          className={cn(btnClass, b.isActive() && "bg-accent text-foreground")}
          onClick={b.run}
          disabled={disabled}
          title={b.label}
          aria-label={b.label}
          aria-pressed={b.isActive()}
        >
          {b.icon}
        </button>
      ))}
      <button
        type="button"
        className={cn(
          btnClass,
          editor.isActive("link") && "bg-accent text-foreground",
        )}
        onClick={promptLink}
        disabled={disabled}
        title="Link"
        aria-label="Link ekle"
      >
        <LinkIcon className="h-4 w-4" />
      </button>

      <div className="mx-1 h-5 w-px bg-border" aria-hidden />

      <button
        type="button"
        className={btnClass}
        onClick={() => editor.chain().focus().undo().run()}
        disabled={disabled || !editor.can().undo()}
        title="Geri al"
        aria-label="Geri al"
      >
        <Undo className="h-4 w-4" />
      </button>
      <button
        type="button"
        className={btnClass}
        onClick={() => editor.chain().focus().redo().run()}
        disabled={disabled || !editor.can().redo()}
        title="Yinele"
        aria-label="Yinele"
      >
        <Redo className="h-4 w-4" />
      </button>
    </div>
  );
}

/**
 * Read-only Markdown preview — aynı TipTap + tiptap-markdown pipeline'ı ile
 * render edilir, böylece platform default template ile editör çıktısı birebir
 * aynı tipografiye sahip olur.
 */
export function MarkdownPreview({ markdown }: { markdown: string }) {
  // Previewu da TipTap ile render ediyoruz — böylece default + override aynı
  // tipografiye sahip olur. Editable false.
  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({ codeBlock: false, horizontalRule: false }),
      Markdown.configure({ html: false, tightLists: true }),
    ],
    content: markdown,
    editable: false,
    editorProps: {
      attributes: {
        class: "tiptap-prose p-4",
      },
    },
  });

  useEffect(() => {
    // emitUpdate: false → setContent programmatically içeriği değiştirir ama
    // onUpdate hook'unu tetiklemez. (Preview read-only olduğu için önemli değil
    // ama doğru pattern.)
    if (editor) editor.commands.setContent(markdown, { emitUpdate: false });
  }, [editor, markdown]);

  if (!editor) {
    return <div className="rounded-md border bg-muted/30 p-4" aria-busy />;
  }

  return (
    <div className="overflow-hidden rounded-md border bg-background">
      <EditorContent editor={editor} />
    </div>
  );
}
